<ng-container *ngIf="presentation === 'inline'">
  <ng-container *ngTemplateOutlet="testBody"></ng-container>
</ng-container>
<ng-template #testBody>
  <ng-container *ngIf="testView as view">
    <section
      *ngIf="opened"
      [id]="idPrefix + '-panel'"
      tabindex="-1"
      [attr.aria-label]="
        presentation === 'modal'
          ? 'Eligibility test for version ' + versionCode
          : null
      "
      data-hearing-eligibility-test
      class="eligibility-test"
      [class.eligibility-test--modal]="presentation === 'modal'"
      [attr.aria-labelledby]="
        presentation === 'inline' ? idPrefix + '-heading' : null
      "
    >
      <header>
        <div>
          <h3 *ngIf="presentation === 'inline'" [id]="idPrefix + '-heading'">
            Test version {{ versionCode }}
          </h3>
          <p>
            Run eligibility with fresh Initial-hearing test cases, then open the
            hearing workflows created by this version.
          </p>
        </div>
        <button
          uiButton
          type="button"
          [text]="true"
          (click)="close(view)"
          aria-label="Close eligibility test"
        >
          Close
        </button>
      </header>
      <p *ngIf="error" role="alert">{{ error }}</p>
      <ng-container *ngIf="!result; else testResults">
        <form (ngSubmit)="start(view)">
          <div class="test-inputs">
            <div>
              <strong>Test cases</strong>
              <p>20 fresh synthetic Initial-hearing cases</p>
            </div>
            <label
              >Eligibility date<input
                uiControl
                name="eligibilityDate"
                type="date"
                required
                #dateModel="ngModel"
                [id]="idPrefix + '-date'"
                [ngModel]="eligibilityDate"
                (ngModelChange)="changedDate(view, $event, dateModel)"
                [disabled]="starting || interactionBlocked"
            /></label>
          </div>
          <p>
            Starting a test creates 22 synthetic people, 20 Initial-hearing test
            cases and qualifying hearing workflows. Earlier tests and the live
            version selection are retained. Closing does not cancel a queued
            test.
          </p>
          <p *ngIf="checking" role="status">Checking readiness…</p>
          <div *ngIf="readiness && !readiness.ready" role="alert">
            <h4>Resolve these items before testing</h4>
            <ul>
              <li *ngFor="let blocker of readiness.blockers">
                <strong>{{ blocker.label }}:</strong> {{ blocker.message }}
                <a
                  *ngIf="blocker.actionUrl; else deploymentOwner"
                  [routerLink]="actionLink(blocker.actionUrl)"
                  (click)="close(view)"
                  >{{ blocker.owner }}</a
                >
                <ng-template #deploymentOwner
                  ><span> {{ blocker.owner }}</span></ng-template
                >
              </li>
            </ul>
            <button
              uiButton
              type="button"
              (click)="check(view)"
              [disabled]="checking"
            >
              Check again
            </button>
          </div>
          <button
            *ngIf="presentation === 'modal' && !readiness && !checking"
            uiButton
            type="button"
            (click)="check(view)"
            [disabled]="starting || interactionBlocked"
          >
            Check readiness
          </button>
          <button
            uiButton
            type="submit"
            severity="primary"
            [outlined]="false"
            [disabled]="
              !readiness?.ready || checking || starting || interactionBlocked
            "
          >
            {{ starting ? 'Starting test…' : 'Start test' }}
          </button>
        </form>
        <details *ngIf="history.length">
          <summary>Recent tests of this version</summary>
          <ul>
            <li *ngFor="let run of history">
              <button
                uiButton
                type="button"
                [text]="true"
                (click)="resume(run.runId, false, view)"
              >
                {{ run.createdUtc | date: 'short' }} ·
                {{ statusLabel(run.status) }}
              </button>
            </li>
          </ul>
        </details>
      </ng-container>
      <ng-template #testResults>
        <ng-container *ngIf="result as run">
          <div class="test-progress" role="status" aria-live="polite">
            <strong>{{ statusLabel(run.status) }}</strong>
            <span
              >Version {{ run.versionCode }} · Eligibility date
              {{ run.eligibilityDate | date: 'mediumDate' }}</span
            >
            <span *ngIf="run.candidateCount"
              >{{ run.processedCount }} of {{ run.candidateCount }} cases
              processed</span
            >
          </div>
          <p *ngIf="run.status === 'blocked'">
            The test stopped safely. Ask a platform administrator to review the
            issue below. You can run a fresh test after it is resolved.
          </p>
          <div class="test-counts" *ngIf="run.processedCount">
            <div *ngFor="let category of categories">
              <strong>{{ run.counts[category.key] || 0 }}</strong
              ><span>{{ category.label }}</span>
            </div>
          </div>
          <p *ngIf="!running && run.processedCount">
            The test cases include incomplete facts, an unresolved policy and a
            processing failure to exercise those outcomes.
          </p>
          <div class="test-actions">
            <button
              uiButton
              type="button"
              (click)="resume(run.runId, false, view)"
              [disabled]="refreshing"
            >
              Refresh
            </button>
            <button
              uiButton
              type="button"
              *ngIf="!running"
              (click)="freshTest(view)"
              [disabled]="interactionBlocked"
            >
              Run another fresh test
            </button>
          </div>
          <section aria-label="Eligibility test records">
            <h4>Test records ({{ recordRows.length }})</h4>
            <phms-workflow-record-grid
              label="Eligibility test records"
              [rows]="recordRows"
              [columns]="columns"
              [compactRows]="true"
              [reportCount]="false"
            />
          </section>
          <details *ngIf="run.issueCode">
            <summary>Technical details for administrator</summary>
            <code>{{ run.issueCode }}</code>
          </details>
        </ng-container>
      </ng-template>
    </section>
  </ng-container>
</ng-template>

<ng-template #workflowCell let-record>
  <a *ngIf="record.workflowInstanceId; else noWorkflow" [routerLink]="['/workflows', record.workflowInstanceId]"
    [attr.aria-label]="'Open hearing workflow for ' + (record.inmateName || 'test case ' + record.ordinal)"
    [title]="record.publicReference || 'Open hearing workflow'">Open hearing</a>
  <ng-template #noWorkflow><span class="muted">—</span></ng-template>
</ng-template>
<ng-template #detailsCell let-record>
  <button uiButton type="button" [text]="true" (click)="openDetails(record)"
    [attr.aria-label]="'View details for test case ' + record.ordinal">Details</button>
</ng-template>
<ng-template #recordDetails>
  <section *ngIf="selectedRecord as record" class="record-details">
    <header><h3>Test case {{ record.ordinal }}</h3><button uiButton type="button" (click)="closeDetails()">Close details</button></header>
    <dl><dt>Inmate</dt><dd>{{ record.inmateName || 'Name unavailable' }}</dd>
      <dt>Inmate number</dt><dd>{{ record.inmateNumber || 'Not recorded' }}</dd>
      <dt>Location</dt><dd>{{ record.institutionName || 'Not recorded' }}</dd>
      <dt>Outcome</dt><dd>{{ record.outcome }}</dd>
      <ng-container *ngIf="record.publicReference"><dt>Workflow reference</dt><dd>{{ record.publicReference }}</dd></ng-container></dl>
    <p>{{ record.explanation }}</p>
    <details *ngIf="record.issueCode"><summary>Diagnostic details</summary><code>{{ record.issueCode }}</code></details>
  </section>
</ng-template>
